import { Component, OnInit, ViewChild } from "@angular/core";
import languageNames from "../const/languageNames.json";
import languageCodes from "../const/languageCodes.json";
import {ActivatedRoute, Router} from "@angular/router";
import {MatTree, MatTreeFlattener, MatTreeNestedDataSource} from "@angular/material/tree";
import { NestedTreeControl } from "@angular/cdk/tree";
import { FeatureAccessRightsComponent } from "../dialogs/feature-access-rights/feature-access-rights.component";
import { MatDialog } from "@angular/material/dialog";
import { FeatureBusinessRuleComponent } from "../dialogs/feature-business-rule/feature-business-rule.component";
import {DataCatalogueService} from "../services/data-catalogue.service";
import {EMPTY, expand, first, from, last, Observable} from "rxjs";
import {map, mergeMap, tap} from "rxjs/operators";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {FeatureVersion} from "../model/catalog-item";
import {MatSnackBar} from "@angular/material/snack-bar";

interface TreeNode {
  name: string;
  children?: TreeNode[];
  expanded?: boolean;
}

@Component({
  selector: "feature-view",
  templateUrl: "./feature-view.component.html",
  styleUrls: ["./feature-view.component.css"],
})
export class FeatureViewComponent implements OnInit {
  id: string | null = null;
  @ViewChild(MatTree) tree: MatTree<TreeNode>;
  dataSource = new MatTreeNestedDataSource<TreeNode>();
  treeControl = new NestedTreeControl<TreeNode>(node => node.children);
  propertyName: string;
  catId: string = '';
  metaData: any;
  occurrences: number | undefined = undefined;
  form = new FormGroup({
    displayName: new FormArray([]),
    description: new FormArray([])
  });
  currentDateString: string = new Date()?.toLocaleDateString('en-GB', {
    day:   'numeric',
    month: 'short',
    year:  'numeric'
  });

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private dataCatalogueService:
        DataCatalogueService,
    private router: Router,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
  ) {}

  openLockDialog() {
    const dialogRef = this.dialog.open(FeatureAccessRightsComponent, {
      width: "1000px",
      data: {
        feature: this.id,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("Dijalog zatvoren, rezultat:", result);
    });
  }

  initArrays() {
    this.metaData.displayName.forEach(item => {
      this.displayNameArray.push(
          new FormGroup({
            lang: new FormControl({ value: item.lang, disabled: true }),
            text: new FormControl(item.text)
          })
      );
    });

    this.metaData.description.forEach(item => {
      this.descriptionArray.push(
          new FormGroup({
            lang: new FormControl({ value: item.lang, disabled: true }),
            text: new FormControl(item.text)
          })
      );
    });
  }

  get displayNameArray() {
    return this.form.get('displayName') as FormArray;
  }

  get descriptionArray() {
    return this.form.get('description') as FormArray;
  }

  openBusinessRuleDialog() {
    const dialogRef = this.dialog.open(FeatureBusinessRuleComponent, {
      width: "1000px",
      height: "600px",

      data: {
        feature: this.id,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("Dijalog zatvoren, rezultat:", result);
    });
  }

  ngAfterViewInit() {
    // this.expandAll();
  }

  expandAll() {
    this.dataSource.data?.forEach(node => this.expandNodeRecursive(node, 1));
  }

  expandNodeRecursive(node: TreeNode, level: number) {
    if (node.children && level <= 2) {
      this.treeControl.expand(node);
      node.children.forEach(child => this.expandNodeRecursive(child, level + 1));
    }
  }

  childrenAccessor = (node: any) => node.children;
  hasChild = (_: number, node: TreeNode) =>
    !!node.children && node.children.length > 0;

  mapCatalogDtoToNode(dto: any): TreeNode {
    return {
      name: dto?.name,
      children: []
    };
  }

  getLanguageName(code: string) : string {
    if (!!languageNames[code]) {
      return languageNames[code];
    } else return '';
  }

  fetchTreeData = (itemId: string): Observable<any> => {
      let root: any;

      return this.dataCatalogueService.getItemMetadata(this.catId, 'features', 'aixm', itemId).pipe(
        map((dto) => {
          let metadata = dto?.versions?.sort((a: FeatureVersion, b:FeatureVersion) => {
            if (a?.timestamp > b?.timestamp) return 1;
            return -1;
          })?.[0];
          const updatedDate = new Date(metadata?.timestamp);
          metadata.timestamp = updatedDate?.toLocaleDateString('en-GB', {
            day:   'numeric',
            month: 'short',
            year:  'numeric'
          });
          this.metaData = metadata;
          root = this.mapCatalogDtoToNode(dto);
          if (root?.name?.includes(":")) root.name = root?.name?.split(":").pop();
          return root;
        }),
        expand((parent: any) => {

          const name = parent?.name?.split(":")?.pop() || parent?.name;
          if (parent?.type === 'property') return EMPTY;
          if (parent?.type === 'choice') return EMPTY;
          return this.dataCatalogueService.getItemMetadata(this.catId, parent?.type ? parent?.type : 'features', 'aixm', name).pipe(
            map((dto: any) =>
              (dto?.versions?.[0]?.content ?? []).map((c) => {
                const contentModelEntryType = c?.contentModelEntryType;
                const catalogItemType = c?.catalogItemType;
                const resolvedType = contentModelEntryType ? contentModelEntryType : catalogItemType ? catalogItemType : 'features';
                const type =
                    resolvedType.includes('propertyGroup')
                        ? 'groups'
                        : resolvedType.includes('feature')
                            ? 'features'
                            : resolvedType.includes('choice')
                              ? 'choice'
                                : 'property';
                const name =
                    c?.name?.split(':')?.[1] ??
                    c?.ref?.split(':')?.[1];
                if (type === 'property') {
                  return { name, type, propertyId: c?.id };
                }
                if (type === 'choice') {
                  const options = [];
                  c?.options?.forEach((option) => {
                    const subOptions = [];
                    if (option?.choiceOptionType === 'choice' && option?.options?.length > 0) {
                      option?.options?.forEach((subOption) => {
                        subOptions.push({
                          name: subOption?.name?.split('_')?.[1],
                          id: subOption?.id,
                          propertyId: subOption?.id
                        });
                      });
                      options.push({
                        type: 'choice',
                        options: subOptions
                      });
                    } else {
                      options.push({
                        name: option?.name?.split('_')?.[1],
                        id: option?.id,
                        propertyId: option?.id
                      });
                    }
                  });

                  return { type, options }
                }

                return { name, type, children: [] };
              })
            ),
            tap(children => parent.children.push(...children)),
            mergeMap(children => from(children))
          )
        }),
        last(),
        map(() => root)
      );
  };

  fetchOccurrences() {
    this.dataCatalogueService.getItemCount(this.catId, 'features', 'aixm', this.id).subscribe((res: number) => {
        this.occurrences = res;
    })
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get("id");
    this.catId = this.route.snapshot.paramMap.get("catId");

    this.route.paramMap.subscribe((params) => {
      this.id = params.get("id");
      this.catId = params.get("catId");
    });

    this.fetchTreeData(this.id).subscribe((res) => {
      this.dataSource.data = res?.children;
      this.expandAll();
      this.initArrays();
    });

    this.fetchOccurrences();
  }

  save(): void {
    const displayNameArr = [];
    const descriptionArr = [];
    this.form.getRawValue()?.displayName?.forEach((el: any) => {
      displayNameArr.push({
        lang: el?.lang + ', ' + languageCodes?.[el?.lang],
        text: el?.text
      });
    });
    this.form.getRawValue()?.description?.forEach((el: any) => {
      descriptionArr.push({
        lang: el?.lang + ', ' + languageCodes?.[el?.lang],
        text: el?.text
      })
    });

    this.dataCatalogueService.updateItemMetadata(this.catId, 'features', 'aixm', this.id, {
      displayName: displayNameArr,
      description: descriptionArr
    }).subscribe((res) => {
      this.snackBar.open(`Changes successfully saved. `, "OK", {
        duration: 2000,
      });
    });
  }
}
